import './TransactionFilterComponent.scss';
import { DatePicker, Drawer, message, Select, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import FilterButton from 'app/components/common/Checks/FilterButton';
import { transactionProcessingStatusMap } from '../colorScheme';
import { TAG_DISPLAY_NAMES } from 'app/components/common/Tags/tagUtils';
import { useFlags } from 'flagsmith/react';
import { getTransactionMilestonesForPartner } from 'app/configs/transaction';
import { getCompliancePartnerID } from 'app/utils/localStorageHandler/userProfile';
import { ComplianceService } from 'app/services/compliance';
import useDebounce from 'app/hooks/UseDebounce';

interface SelectOption {
  value: string;
  label: string;
  fixed?: boolean;
}

const statuses: SelectOption[] = [
  {
    value: 'NOT_STARTED',
    label: transactionProcessingStatusMap.NOT_STARTED.name,
  },
  {
    value: 'PROCESSING',
    label: transactionProcessingStatusMap.PROCESSING.name,
  },
  {
    value: 'MAX_APPROVED',
    label: transactionProcessingStatusMap.MAX_APPROVED.name,
  },
  {
    value: 'USER_APPROVED',
    label: transactionProcessingStatusMap.USER_APPROVED.name,
  },
  {
    value: 'SENT_TO_AGENT_FOR_UPDATES',
    label: transactionProcessingStatusMap.SENT_TO_AGENT_FOR_UPDATES.name,
  },
  {
    value: 'DOCS_MISSING',
    label: transactionProcessingStatusMap.DOCS_MISSING.name,
  },
  {
    value: 'VALIDATION_FAILED',
    label: transactionProcessingStatusMap.VALIDATION_FAILED.name,
  },
  {
    value: 'NOT_APPLICABLE',
    label: transactionProcessingStatusMap.NOT_APPLICABLE.name,
  },
];

interface TransactionFilterComponentProps {
  selectedStatuses: string[];
  selectedAgents?: string[];
  selectedTransactionTypes: string[];
  selectedTransactionMilestones: string[];
  createdAtFilter: { gte: string; lte: string };
  closingDateFilter: { gte: string; lte: string };
  updatedAtFilter: { gte: string; lte: string };
  selectedTags: string[];
  onFilterChange: (
    statuses: string[],
    selectedAgents: string[],
    createdAtFilter: { gte: string; lte: string },
    closingDateFilter: { gte: string; lte: string },
    transactionTypes: string[],
    transactionMilestones: string[],
    updatedAtFilter: { gte: string; lte: string },
    tags: string[]
  ) => void;
  onClearFilters: () => void;
  activeFiltersCount: number;
  fixedTags?: string[];
}

const TransactionFilterComponent: React.FC<TransactionFilterComponentProps> = ({
  selectedStatuses,
  selectedAgents,
  selectedTransactionTypes,
  selectedTransactionMilestones,
  createdAtFilter,
  closingDateFilter,
  updatedAtFilter,
  onFilterChange,
  onClearFilters,
  activeFiltersCount,
  selectedTags,
  fixedTags = [],
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [agentOptions, setAgentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 300);

  const [searchingAgents, setSearchingAgents] = useState(false);

  const toggleDropdown = () => {
    setShowDropdown((prev) => {
      const isClosing = prev === true;
      if (isClosing) {
        setSearchText('');
      }
      return !prev;
    });
  };
  const transactionTypeOptions = [
    { value: 'PURCHASE', label: 'Buy' },
    { value: 'LISTING', label: 'List' },
    { value: 'LEASE_OFFER', label: 'Lease Offer' },
    { value: 'LEASE_LISTING', label: 'Lease List' },
  ];

  const transactionMilestonesOptions = Object.entries(
    getTransactionMilestonesForPartner(getCompliancePartnerID())
  ).map(([milestone, displayName]) => ({
    value: milestone,
    label: displayName,
  }));

  const tagFlags = useFlags(['show_tags']);
  const isTagEnabled = tagFlags?.show_tags?.enabled;

  useEffect(() => {
    const fetchAgents = async () => {
      setSearchingAgents(true);
      try {
        const response = await ComplianceService.getAgents(
          debouncedSearch || ''
        );
        const agents = (response.data?.data || []).map((agent: any) => ({
          value: agent._id,
          label: agent.name,
        }));
        setAgentOptions(agents);
      } catch (error) {
        console.error('Error fetching agents:', error);
        message.error('Failed to fetch agents');
      } finally {
        setSearchingAgents(false);
      }
    };

    // Fetch agents on search OR when drawer opens (debouncedSearch can be empty)
    if (showDropdown) {
      fetchAgents();
    }
  }, [debouncedSearch, showDropdown]);

  const handleStatusSelect = (value: string[]) => {
    onFilterChange(
      value,
      selectedAgents || [],
      createdAtFilter,
      closingDateFilter,
      selectedTransactionTypes,
      selectedTransactionMilestones,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleAgentSelect = (value: string[]) => {
    onFilterChange(
      selectedStatuses,
      value,
      createdAtFilter,
      closingDateFilter,
      selectedTransactionTypes,
      selectedTransactionMilestones,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleTransactionTypeSelect = (value: string[]) => {
    onFilterChange(
      selectedStatuses,
      selectedAgents || [],
      createdAtFilter,
      closingDateFilter,
      value,
      selectedTransactionMilestones,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleTransactionMilestoneSelect = (value: string[]) => {
    onFilterChange(
      selectedStatuses,
      selectedAgents || [],
      createdAtFilter,
      closingDateFilter,
      selectedTransactionTypes,
      value,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleCreatedDateChange = (value: any) => {
    const newCreatedAtFilter = {
      gte: value?.[0]?.format('MM-DD-YYYY') || '',
      lte: value?.[1]?.format('MM-DD-YYYY') || '',
    };
    onFilterChange(
      selectedStatuses,
      selectedAgents || [],
      newCreatedAtFilter,
      closingDateFilter,
      selectedTransactionTypes,
      selectedTransactionMilestones,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleClosingDateChange = (value: any) => {
    const newClosingDateFilter = {
      gte: value?.[0]?.format('MM-DD-YYYY') || '',
      lte: value?.[1]?.format('MM-DD-YYYY') || '',
    };
    onFilterChange(
      selectedStatuses,
      selectedAgents || [],
      createdAtFilter,
      newClosingDateFilter,
      selectedTransactionTypes,
      selectedTransactionMilestones,
      updatedAtFilter,
      selectedTags
    );
  };

  const handleUpdatedDateChange = (value: any) => {
    const newUpdatedAtFilter = {
      gte: value?.[0]?.format('MM-DD-YYYY') || '',
      lte: value?.[1]?.format('MM-DD-YYYY') || '',
    };
    onFilterChange(
      selectedStatuses,
      selectedAgents || [],
      createdAtFilter,
      closingDateFilter,
      selectedTransactionTypes,
      selectedTransactionMilestones,
      newUpdatedAtFilter,
      selectedTags
    );
  };

  const tagValueLabelMap = TAG_DISPLAY_NAMES;
  const tagOptionsWithFixed: SelectOption[] = Object.entries(
    tagValueLabelMap
  ).map(([value, label]) => ({
    value,
    label,
    fixed: fixedTags?.includes(value),
  }));

  // Fixed tags are always shown in the selected tags list, but not in the options
  const filteredTagOptions: SelectOption[] = tagOptionsWithFixed.filter(
    (option) => !option.fixed
  );

  const selectTagRender = (props: {
    label: React.ReactNode;
    value: string;
    closable: boolean;
    onClose: (e: React.MouseEvent<HTMLElement>) => void;
  }) => {
    const { value, closable, onClose } = props;
    const tagOption = tagOptionsWithFixed.find((opt) => opt.value === value);
    const isFixed = tagOption?.fixed;
    const displayLabel = tagOption?.label || value;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={!isFixed && closable}
        onClose={onClose}
        style={{
          margin: '0.25rem',
          padding: '0.3rem',
          fontSize: '0.9rem',
          borderRadius: '16px',
          // Style the fixed tags differently
          background: isFixed ? '#e6f7ff' : undefined,
          color: isFixed ? '#1890ff' : undefined,
          fontWeight: isFixed ? 600 : undefined,
        }}
      >
        {displayLabel}
      </Tag>
    );
  };

  return (
    <div className="filter-component">
      <FilterButton
        onClick={toggleDropdown}
        clearFilters={onClearFilters}
        selectedCount={activeFiltersCount}
      />

      <Drawer
        title={<Typography fontSize="1.5rem">Filters</Typography>}
        open={showDropdown}
        onClose={toggleDropdown}
        width={350}
      >
        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Status
          </Typography>
          <Select
            mode="multiple"
            className="select-component"
            placeholder="Select Status"
            value={selectedStatuses}
            onChange={handleStatusSelect}
            options={statuses}
            allowClear
          />
        </div>
        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Agents
          </Typography>
          <Select
            mode="multiple"
            className="select-component"
            placeholder="Select Agents"
            value={selectedAgents}
            onChange={handleAgentSelect}
            options={agentOptions}
            allowClear
            onSearch={(value) => setSearchText(value)}
            filterOption={false}
            loading={searchingAgents}
            showSearch
          />
        </div>

        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Transaction Type
          </Typography>
          <Select
            mode="multiple"
            className="select-component"
            placeholder="Select Transaction Type"
            value={selectedTransactionTypes}
            onChange={handleTransactionTypeSelect}
            options={transactionTypeOptions}
            allowClear
          />
        </div>

        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Transaction Milestone
          </Typography>
          <Select
            mode="multiple"
            className="select-component"
            placeholder="Select Transaction Milestone"
            value={selectedTransactionMilestones}
            onChange={handleTransactionMilestoneSelect}
            options={transactionMilestonesOptions}
            allowClear
          />
        </div>

        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Created Date
          </Typography>
          <DatePicker.RangePicker
            disabledDate={(current) => current > dayjs()}
            value={
              createdAtFilter.gte && createdAtFilter.lte
                ? [dayjs(createdAtFilter.gte), dayjs(createdAtFilter.lte)]
                : null
            }
            onChange={handleCreatedDateChange}
          />
        </div>

        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Closing Date
          </Typography>
          <DatePicker.RangePicker
            value={
              closingDateFilter.gte && closingDateFilter.lte
                ? [dayjs(closingDateFilter.gte), dayjs(closingDateFilter.lte)]
                : null
            }
            onChange={handleClosingDateChange}
          />
        </div>

        <div className="filter-section">
          <Typography fontSize="1rem" marginBottom="0.5rem">
            Last Updated Date
          </Typography>
          <DatePicker.RangePicker
            value={
              updatedAtFilter.gte && updatedAtFilter.lte
                ? [dayjs(updatedAtFilter.gte), dayjs(updatedAtFilter.lte)]
                : null
            }
            onChange={handleUpdatedDateChange}
          />
        </div>

        {isTagEnabled ? (
          <div className="filter-section">
            <Typography fontSize="1rem" marginBottom="0.5rem">
              Tags
            </Typography>
            <Select
              mode="multiple"
              // Don't allow when selected tags are fixed tags
              allowClear={
                fixedTags.length > 0
                  ? selectedTags.length > fixedTags.length
                  : true
              }
              style={{ width: '100%', marginBottom: '1rem' }}
              placeholder="Select Tags"
              value={selectedTags}
              onChange={(values) => {
                onFilterChange(
                  selectedStatuses,
                  selectedAgents || [],
                  createdAtFilter,
                  closingDateFilter,
                  selectedTransactionTypes,
                  selectedTransactionMilestones,
                  updatedAtFilter,
                  values
                );
              }}
              options={filteredTagOptions}
              tagRender={selectTagRender}
            />
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default TransactionFilterComponent;
